import React, { useState } from "react";
import { LuPlus, LuTrash2, LuCheck, LuX, LuCircleHelp } from "react-icons/lu";

const McqBuilderModal = ({ isOpen, onClose, onInsert }) => {
  const [quizTitle, setQuizTitle] = useState("UPSC Rank & Services MCQ Test");
  const [quizSubtitle, setQuizSubtitle] = useState("Test your understanding of the key concepts covered in this article.");
  const [questions, setQuestions] = useState([
    {
      question: "Which factors can influence UPSC service allocation?",
      options: [
        { label: "a", text: "Only the final rank" },
        { label: "b", text: "Rank, category, vacancies, preferences and eligibility" },
        { label: "c", text: "Only interview marks" },
        { label: "d", text: "Only the candidate's age" },
      ],
      correctAnswer: "b",
    },
    {
      question: "Which is the correct broad sequence of the Civil Services Examination?",
      options: [
        { label: "a", text: "Mains → Prelims → Interview" },
        { label: "b", text: "Interview → Prelims → Mains" },
        { label: "c", text: "Prelims → Mains → Personality Test" },
        { label: "d", text: "Prelims → Interview → Mains" },
      ],
      correctAnswer: "c",
    },
  ]);
  const [previewTab, setPreviewTab] = useState("edit"); // edit | preview

  if (!isOpen) return null;

  // Add Question
  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        question: `New question ${questions.length + 1}?`,
        options: [
          { label: "a", text: "Option A" },
          { label: "b", text: "Option B" },
          { label: "c", text: "Option C" },
          { label: "d", text: "Option D" },
        ],
        correctAnswer: "a",
      },
    ]);
  };

  // Remove Question
  const handleRemoveQuestion = (idx) => {
    if (questions.length <= 1) {
      alert("At least one question is required!");
      return;
    }
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  // Update Question text
  const handleQuestionTextChange = (idx, text) => {
    const updated = [...questions];
    updated[idx].question = text;
    setQuestions(updated);
  };

  // Update Option text
  const handleOptionTextChange = (qIdx, optIdx, text) => {
    const updated = [...questions];
    updated[qIdx].options[optIdx].text = text;
    setQuestions(updated);
  };

  // Update Correct Answer
  const handleCorrectAnswerChange = (qIdx, label) => {
    const updated = [...questions];
    updated[qIdx].correctAnswer = label;
    setQuestions(updated);
  };

  // Generate HTML & insert
  const handleInsertQuiz = () => {
    const html = `
<section id="achiwon-quiz" class="mt-14">
  <div class="mb-5">
    <p class="text-xs font-bold uppercase tracking-[0.15em] text-primary">Quick Test</p>
    <h2 class="mt-2 font-heading text-2xl font-bold">${quizTitle.trim()}</h2>
    ${
      quizSubtitle.trim()
        ? `<p class="mt-3 text-muted-foreground">${quizSubtitle.trim()}</p>`
        : ""
    }
  </div>
  <div class="mcq-box">
    ${questions
      .map(
        (q, idx) => `
    <div class="mcq-question" data-answer="${q.correctAnswer}">
      <p>${idx + 1}. ${q.question}</p>
      ${q.options
        .map(
          (opt) => `
      <label class="mcq-option">
        <input type="radio" name="q${idx + 1}" value="${opt.label}">
        <span>${opt.text}</span>
      </label>`
        )
        .join("")}
    </div>`
      )
      .join("")}
    <button type="button" class="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-5 py-3 text-sm font-bold text-white hover:opacity-90 cursor-pointer">Submit Test</button>
    <div class="mcq-result" hidden></div>
  </div>
</section>
`;
    onInsert(html.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#114598] text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-white/10 rounded-lg">
              <LuCircleHelp className="text-xl text-[#F68122]" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Interactive MCQ Quiz Builder</h2>
              <p className="text-xs text-white/80">
                Create engaging MCQ tests with automated grading for students.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <LuX className="text-xl" />
          </button>
        </div>

        {/* Subheader */}
        <div className="px-6 py-3 bg-[#F8FAFC] border-b border-border flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-600">
            Total {questions.length} Questions configured
          </span>
          <div className="bg-white border border-border rounded-lg p-0.5 flex">
            <button
              type="button"
              onClick={() => setPreviewTab("edit")}
              className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                previewTab === "edit"
                  ? "bg-[#114598] text-white"
                  : "text-slate-600 hover:text-primary"
              }`}
            >
              Quiz Builder
            </button>
            <button
              type="button"
              onClick={() => setPreviewTab("preview")}
              className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                previewTab === "preview"
                  ? "bg-[#114598] text-white"
                  : "text-slate-600 hover:text-primary"
              }`}
            >
              Live Preview
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-6 custom-scrollbar">
          {previewTab === "edit" ? (
            <>
              {/* Quiz Header Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Quiz Title
                  </label>
                  <input
                    type="text"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e.target.value)}
                    placeholder="e.g. UPSC Rank & Services MCQ Test"
                    className="w-full px-3 py-2 border border-border rounded-xl text-xs bg-white focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Quiz Subtitle / Instructions
                  </label>
                  <input
                    type="text"
                    value={quizSubtitle}
                    onChange={(e) => setQuizSubtitle(e.target.value)}
                    placeholder="e.g. Test your understanding of key concepts..."
                    className="w-full px-3 py-2 border border-border rounded-xl text-xs bg-white focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Questions List */}
              <div className="space-y-5">
                {questions.map((q, qIdx) => (
                  <div
                    key={qIdx}
                    className="p-5 border border-slate-200 rounded-xl bg-white shadow-xs relative"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-extrabold uppercase px-2.5 py-1 bg-blue-50 text-[#114598] rounded-md">
                        Question {qIdx + 1}
                      </span>
                      {questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(qIdx)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 text-xs font-bold inline-flex items-center gap-1 cursor-pointer"
                        >
                          <LuTrash2 className="text-sm" /> Delete Question
                        </button>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Question Statement:
                      </label>
                      <input
                        type="text"
                        value={q.question}
                        onChange={(e) => handleQuestionTextChange(qIdx, e.target.value)}
                        placeholder="Write question here..."
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-primary focus:outline-none"
                      />
                    </div>

                    {/* Options Grid */}
                    <div className="space-y-2 mt-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Options (Mark the radio circle for the correct answer):
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {q.options.map((opt, optIdx) => (
                          <div
                            key={opt.label}
                            className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                              q.correctAnswer === opt.label
                                ? "border-emerald-500 bg-emerald-50/50"
                                : "border-slate-200 bg-slate-50/50"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`correct_${qIdx}`}
                              checked={q.correctAnswer === opt.label}
                              onChange={() => handleCorrectAnswerChange(qIdx, opt.label)}
                              className="accent-emerald-600 cursor-pointer"
                              title="Set as correct answer"
                            />
                            <span className="text-xs font-bold uppercase text-slate-500 w-4">
                              {opt.label})
                            </span>
                            <input
                              type="text"
                              value={opt.text}
                              onChange={(e) =>
                                handleOptionTextChange(qIdx, optIdx, e.target.value)
                              }
                              className="w-full px-2 py-1 bg-white border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                              placeholder={`Option ${opt.label.toUpperCase()} text`}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-emerald-700 font-medium">
                        ✓ Correct Answer is set to: Option{" "}
                        <strong className="uppercase">{q.correctAnswer}</strong>
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Question Button */}
              <button
                type="button"
                onClick={handleAddQuestion}
                className="w-full py-3 border-2 border-dashed border-slate-300 hover:border-[#114598] hover:text-[#114598] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 text-slate-600 cursor-pointer"
              >
                <LuPlus className="text-base" /> + Add Another Question
              </button>
            </>
          ) : (
            /* Live Preview */
            <div className="bg-[#F8FAFC] p-6 rounded-2xl border border-border">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                Interactive Quiz Preview:
              </p>
              <section className="mcq-box">
                <div className="mb-4">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#114598]">
                    Quick Test
                  </p>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">
                    {quizTitle}
                  </h3>
                  {quizSubtitle && (
                    <p className="text-xs text-slate-500 mt-1">{quizSubtitle}</p>
                  )}
                </div>

                {questions.map((q, idx) => (
                  <div key={idx} className="mcq-question">
                    <p className="text-sm font-bold text-slate-900">
                      {idx + 1}. {q.question}
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {q.options.map((opt) => (
                        <div
                          key={opt.label}
                          className="mcq-option"
                        >
                          <input
                            type="radio"
                            name={`prev_q_${idx}`}
                            id={`prev_q_${idx}_${opt.label}`}
                          />
                          <span className="text-xs">{opt.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="mt-6 inline-flex items-center justify-center rounded-lg bg-[#114598] px-5 py-2.5 text-xs font-bold text-white"
                >
                  Submit Test (Graded dynamically on Landing Page)
                </button>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F8FAFC] border-t border-border flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {questions.length} questions will be inserted into the blog.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleInsertQuiz}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#114598] hover:bg-[#F68122] text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
            >
              <LuCheck className="text-base" /> Insert MCQ Test into Blog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default McqBuilderModal;
